import React from 'react';
import { Box, Button, Typography, Container, Card, CardMedia, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useWebsiteSettings } from '../contexts/WebsiteSettingsContext';

export type EventFeaturedSettings = {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaUrl?: string;
  useGradient?: boolean;
  bgColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
  textColor?: string;
  buttonBg?: string;
  buttonText?: string;
  productIds?: string[];
};

export type SimpleProduct = {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
};

interface Props {
  settings?: EventFeaturedSettings;
  products: SimpleProduct[];
}

const EventFeaturedSection: React.FC<Props> = ({ settings: section, products }) => {
  // Hooks must run unconditionally
  const { settings: siteSettings } = useWebsiteSettings();
  const layout = (siteSettings?.layout || {}) as any;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const maxWidth = !isMobile && typeof layout.container === 'string' && layout.container !== 'false' ? (layout.container as any) : false;
  const px = !isMobile && typeof layout.contentPaddingX === 'number' ? layout.contentPaddingX : 0;
  if (!section || section.enabled === false) return null;
  const useGradient = section.useGradient === true;
  const bgColor = section.bgColor || '#f472b6';
  const from = section.gradientFrom || '#ec4899';
  const to = section.gradientTo || '#f59e0b';
  const textColor = section.textColor || '#111827';
  const btnBg = section.buttonBg || '#111827';
  const btnText = section.buttonText || '#ffffff';

  

  return (
    <Container maxWidth={maxWidth} sx={{ py: { xs: 3, md: 4 }, px: { xs: 1.5, md: px } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
        <Box
          sx={{
            borderRadius: 3,
            p: 3,
            background: useGradient ? `linear-gradient(135deg, ${from} 0%, ${to} 100%)` : bgColor,
            color: textColor,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: { md: 280 },
          }}
        >
          <Box>
            {section.title && (
              <Typography variant="h5" fontWeight={900} sx={{ mb: 1, lineHeight: 1.2 }}>
                {section.title}
              </Typography>
            )}
            {section.subtitle && (
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                {section.subtitle}
              </Typography>
            )}
          </Box>
          {section.ctaUrl && (
            <Button
              variant="contained"
              onClick={() => window.open(section.ctaUrl!, '_blank', 'noopener,noreferrer')}
              sx={{
                alignSelf: 'flex-start',
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 2,
                px: 2.25,
                py: 1,
                background: btnBg,
                color: btnText,
                boxShadow: '0 8px 20px rgba(2,6,23,0.15)',
                '&:hover': { filter: 'brightness(0.95)' },
              }}
            >
              {section.ctaText || 'Jetzt einkaufen'}
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: { xs: '75%', sm: '45%', md: 'minmax(220px, 1fr)' }, gap: 2, overflowX: 'auto', pb: 1 }}>
          {products.map(p => (
            <Card key={p.id} sx={{ borderRadius: 3, minWidth: 220, boxShadow: '0 8px 20px rgba(2,6,23,0.08)' }}>
              <CardMedia component="img" image={p.image} sx={{ height: 160, objectFit: 'cover' }} />
              <CardContent sx={{ pt: 1.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineClamp: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {p.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                  <Typography variant="subtitle1" fontWeight={900}>{p.price?.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Typography>
                  {p.originalPrice && (
                    <Typography variant="body2" sx={{ textDecoration: 'line-through', opacity: 0.6 }}>
                      {p.originalPrice.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default EventFeaturedSection;
